import { useCallback, useEffect, useRef } from "react";

export const useScrollTracking = (): void => {
  const getScrollPercentage = useCallback(() => {
    const scrollTop = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    // Prevent scrollPercentage exceeding 100% due to browser inconsistencies.
    return Math.min(((scrollTop + viewportHeight) / documentHeight) * 100, 100);
  }, []);

  // Used to ensure analytics are not sent multiple times.
  // This is due to different options being passed to the add/removeEventListener calls.
  // Only the `capture` is currently required to match in both add/remove, but this could
  // change in the future.
  let analyticsSent = useRef<boolean>(false);
  const sendAnalytics = useCallback(() => {
    if (analyticsSent.current) {
      return;
    }
    analyticsSent.current = true;
    const w = window as any;
    const gtag = w.gtag;
    if (gtag) {
      gtag("event", "scroll", {
        percent_scrolled: 90
      });
    }
  }, []);

  useEffect(() => {
    if (getScrollPercentage() >= 90) {
      // Maintain current behaviour and send event on page load as well as scroll.
      return sendAnalytics();
    }
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPercentage = getScrollPercentage();
          if (scrollPercentage >= 90) {
            sendAnalytics();
            window.removeEventListener("scroll", scrollListener);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", scrollListener, { passive: true });
    return () => {
      window.removeEventListener("scroll", scrollListener);
    };
  }, [getScrollPercentage, sendAnalytics]);
};
